import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl, SafeScript, SafeStyle, SafeUrl } from '@angular/platform-browser';
import { IBaseModuleComponent } from '@base/component/IBaseComponent';
import { formatDate, formatNumber } from 'devextreme/localization';
import { from, Observable, of } from 'rxjs';
import { BaseDataSourceAbstract } from '@base/datalayer/BaseDataSourceAbstract';
import { FrameworkService } from '@core/framework/framework.service';
import { ResourcesService } from '@core/framework/resources.service';
import { DateHelpers } from '@core/helpers/DateHelpers';
import { Utils } from '@core/helpers/Utils';
import { truncate } from 'lodash';



@Pipe({ name: 'RC', pure: true })
export class RCPipe implements PipeTransform {
    constructor(private resources: ResourcesService) { }
    transform(value: string, component: IBaseModuleComponent, ...args: string[] | number[]): string {
        return this.resources.RC(value, component && component.ModuleName, ...args);
    }
}

@Pipe({ name: 'RCM', pure: true })
export class RCMPipe implements PipeTransform {
    constructor(private resources: ResourcesService) { }
    transform(value: string, moduleName: string): string {
        return this.resources.RC(value, moduleName);
    }
}

@Pipe({ name: 'RCX', pure: false })
export class RCXPipe implements PipeTransform {
    constructor(private resources: ResourcesService) { }
    transform(value: string, component: IBaseModuleComponent): string {
        return this.resources.RC(value, component && component.ModuleName);
    }
}

@Pipe({ name: 'RCMX', pure: false })
export class RCMXPipe implements PipeTransform {
    constructor(private resources: ResourcesService) { }
    transform(value: string, moduleName): string {
        return this.resources.RC(value, moduleName);
    }
}

@Pipe({ name: 'YesNo' })
export class YesNoPipe implements PipeTransform {
    constructor(private resources: ResourcesService) { }
    transform(value: boolean): string {
        return this.resources.RC((value ? "genYes" : 'genNo'));
    }
}


@Pipe({ name: 'YesNoYN' })
export class YesNoPipeYN implements PipeTransform {
    constructor(private resources: ResourcesService) { }
    transform(value: string): string {
        return this.resources.RC((value == 'Y' ? "genYes" : 'genNo'));
    }
}

@Pipe({ name: 'YesEmpty' })
export class YesEmptyPipe implements PipeTransform {
    constructor(private resources: ResourcesService) { }
    transform(value: boolean): string {
        return value ? this.resources.RC('genYes') : '';
    }
}



@Pipe({ name: 'LookupList' })
export class LookupListPipe implements PipeTransform {
    transform(value: any, Entities: any[], propName: string = "Text", sDefault: string = undefined, sNullValue: string = undefined): string {

        //If default value was not set, set it to the original value
        if (sDefault == undefined && value != undefined) sDefault = `${value}`

        if (!Entities || !Entities.length) return sNullValue;
        //Lookup
        var result = Entities.find(entity => entity.Value == value);

        //If not found
        if (result == undefined) return sDefault;

        //Get result from the selected property
        var sReturn = result[propName];

        //Handle null
        if (sReturn == "" || sReturn == undefined) sReturn = sNullValue;

        //Finish
        return sReturn;
    }
}

@Pipe({ name: 'Lookup' })
export class LookupPipe implements PipeTransform {
    transform(value: number, dataSource: BaseDataSourceAbstract | [], propName: string | ((r) => string) = "Text", sDefault: string = undefined, sNullValue: string = undefined, keyName: string = "EntryID"): Observable<string> {

        const getStringFromResult = (result: any) => {
            if (result == undefined) return sDefault;

            let sReturn: string;
            if (typeof propName === 'string')
                sReturn  = result[propName];
            else if (typeof propName === 'function')
                sReturn  = propName(result);

            if (sReturn == "" || sReturn == undefined) sReturn = sNullValue;

            return sReturn;
        }

        //If default value was not set, set it to the original value
        if (sDefault == undefined && value != undefined) sDefault = `${value}`;

        if (Utils.IsEmpty(value)) return of(sDefault);

        if (dataSource == undefined) return of('');

        if ((dataSource as any).Store) {
            const ds = dataSource as BaseDataSourceAbstract;
            //Get value
            var promise = ds.Store.byKey(value)
                .then(result => {
                    return getStringFromResult(result);
                })
                .catch(() => {
                    console.warn(`Lookup value ${value} not found`);
                    return sDefault;
                });

            return from(promise);
        } else {
            let result = (dataSource as []).find(x => x[keyName] == value);
            return of(getStringFromResult(result));
        }
    }
}


@Pipe({ name: 'formatNumberShort' })
export class FormatNumberShortPipe implements PipeTransform {
    constructor() { }
    transform(value: number): string {
        return formatNumber(value, "#,##0.#");
    }
}


@Pipe({ name: 'formatNumberGeneral' })
export class FormatNumberGeneralPipe implements PipeTransform {
    constructor(private framework: FrameworkService) { }
    transform(value: number): string {
        return formatNumber(value, this.framework.CompanyInfo.NumberGeneral);
    }
}


@Pipe({ name: 'formatNumberSum' })
export class FormatNumberSumPipe implements PipeTransform {
    constructor(private framework: FrameworkService) { }
    transform(value: number): string {
        return formatNumber(value, this.framework.CompanyInfo.NumberSum);
    }
}


@Pipe({ name: 'formatNumberPrice' })
export class FormatNumberPricePipe implements PipeTransform {
    constructor(private framework: FrameworkService) { }
    transform(value: number): string {
        return formatNumber(value, this.framework.CompanyInfo.NumberPrice);
    }
}


@Pipe({ name: 'formatNumberQuantity' })
export class FormatNumberQuantityPipe implements PipeTransform {
    constructor(private framework: FrameworkService) { }
    transform(value: number): string {
        return formatNumber(value, this.framework.CompanyInfo.NumberQuantity);
    }
}


@Pipe({ name: 'formatNumberPercent' })
export class FormatNumberPercentPipe implements PipeTransform {
    constructor(private framework: FrameworkService) { }
    transform(value: number): string {
        return formatNumber(value, this.framework.CompanyInfo.NumberPercent);
    }
}


@Pipe({ name: 'formatNumberRate' })
export class FormatNumberRatePipe implements PipeTransform {
    constructor(private framework: FrameworkService) { }
    transform(value: number): string {
        return formatNumber(value, this.framework.CompanyInfo.NumberRate);
    }
}



@Pipe({ name: 'formatDate' })
export class FormatDatePipe implements PipeTransform {
    constructor(private framework: FrameworkService) { }
    transform(value: Date): string {
        if (value == undefined) return undefined;

        const date = value instanceof Date ? value : new Date(value)

        return formatDate(date, this.framework.CompanyInfo.DateFormatLong);
    }
}

@Pipe({ name: 'formatDateShort' })
export class FormatDateShortPipe implements PipeTransform {
    constructor(private framework: FrameworkService) { }
    transform(value: Date | string, fallback: string = undefined): string {
        if (value == undefined) return fallback;

        const date = value instanceof Date ? value : new Date(value)

        return formatDate(date, this.framework.CompanyInfo.DateFormatShort);
    }
}


@Pipe({ name: 'formatDateCompact' })
export class FormatDateCompactPipe implements PipeTransform {
    constructor(private framework: FrameworkService) { }
    transform(value: Date): string {
        if (value == undefined) return undefined;
        return formatDate(value, this.framework.CompanyInfo.DateFormatCompact);
    }
}


@Pipe({ name: 'formatDateApproximate' })
export class FormatDateApproximatePipe implements PipeTransform {
    constructor() { }
    transform(value: Date): string {
        if (value == undefined) return undefined;
        return DateHelpers.GetDateApproximate(value);
    }
}


@Pipe({ name: 'formatDateAtTime' })
export class FormatDateAtTimePipe implements PipeTransform {
    constructor(private framework: FrameworkService,
        private resources: ResourcesService) { }
    transform(value: Date): string {
        if (value == undefined) return undefined;

        const correctValue = value instanceof Date ? value : new Date(value);

        const date = formatDate(correctValue, this.framework.CompanyInfo.DateFormatLong);
        const time = formatDate(correctValue, this.framework.CompanyInfo.TimeFormatShort);

        return `${date} ${this.resources.RC('lblAt')} ${time}`;
    }
}


@Pipe({ name: 'formatDateFull' })
export class FormatFullDateCompactPipe implements PipeTransform {
    constructor(private framework: FrameworkService) { }
    transform(value: Date): string {
        if (value == undefined) return undefined;
        return formatDate(value, this.framework.CompanyInfo.DateFormatFull);
    }
}


@Pipe({ name: 'formatTime' })
export class FormatTimePipe implements PipeTransform {
    constructor(private framework: FrameworkService) { }
    transform(value: Date): string {
        if (value == undefined) return undefined;
        return formatDate(value, this.framework.CompanyInfo.TimeFormatShort);
    }
}

@Pipe({ name: 'formatDayName' })
export class FormatDayNamePipe implements PipeTransform {
    constructor() { }
    transform(value: Date): string {
        if (value == undefined) return undefined;
        return DateHelpers.GetDayName(value.getDay() + 1);
    }
}

@Pipe({ name: 'formatDayNameShort' })
export class FormatDayNameShortPipe implements PipeTransform {
    constructor() { }
    transform(value: Date): string {
        if (value == undefined) return undefined;
        return DateHelpers.GetDayNameShort(value.getDay() + 1);
    }
}

@Pipe({ name: 'formatPriceWithCurrency' })
export class FormatPriceWithCurrencyPipe implements PipeTransform 
{
    constructor(private framework: FrameworkService) { }

    transform(value: number, currency: string = null, isShowZero: boolean = false, isHideCharge: boolean = false, cutCharge: boolean = false): string {
        let result = '';

        if(value == null) value = 0;
        if(value == 0 && !isShowZero) return result;

        result = formatNumber(value, !isHideCharge ? this.framework.CompanyInfo.NumberSum : this.framework.CompanyInfo.NumberGeneral);
        if (cutCharge) { 
            const decimalIdx = result.indexOf(this.framework.CompanyInfo.DecimalSeparator);
            if (decimalIdx !== -1) result = result.substring(0, decimalIdx);
        }

        if(currency != null) {
            result += ' ' + currency;
        }

        return result;
    }

}

@Pipe({ name: 'highlight' })
export class HighlightPipe implements PipeTransform {
    constructor(public sanitizer: DomSanitizer) { }

    transform(text: any, search: string): string {
        if (search == undefined || text == undefined) return text;
        var pattern = search
            .replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&")
            .split(' ')
            .filter((t) => t.length > 0)
            .join('|');

        var regex = new RegExp(pattern, 'gi');

        return search
            ? `${text}`.replace(regex, (match) => `<span class="m4u-search-item-highlight">${match}</span>`)
            : text;
    }
}


@Pipe({ name: "orderBy" })
export class ArrayOrderByPipe implements PipeTransform {
    transform(array: any, field: string): any[] {
        if (!Array.isArray(array)) return;

        array.sort((a: any, b: any) => {
            // null value always on top
            if (a[field] == null) return -1;
            if (b[field] == null) return 1;

            if (a[field] < b[field]) {
                return -1;
            } else if (a[field] > b[field]) {
                return 1;
            } else {
                return 0;
            }
        });

        return array;
    }
}


@Pipe({ name: 'safe' })
export class SafePipe implements PipeTransform {
    constructor(protected sanitizer: DomSanitizer) { }

    public transform(value: string, type: string): SafeHtml | SafeStyle | SafeScript | SafeUrl | SafeResourceUrl {
        switch (type) {
            case 'html':
                return this.sanitizer.bypassSecurityTrustHtml(value);
            case 'style':
                return this.sanitizer.bypassSecurityTrustStyle(value);
            case 'script':
                return this.sanitizer.bypassSecurityTrustScript(value);
            case 'url':
                return this.sanitizer.bypassSecurityTrustUrl(value);
            case 'resourceUrl':
                return this.sanitizer.bypassSecurityTrustResourceUrl(value);
            default:
                return this.sanitizer.bypassSecurityTrustUrl(value);
        }
    }
}

@Pipe({ name: 'stringBr' })
export class StringBrPipe implements PipeTransform {
    constructor() { }
    transform(value: string): string {
        if (!value) return value;
        return value.replace(/[\r\n]+/g, '<br/>');
    }
}

@Pipe({ name: 'fileSize' })
export class FileSizePipe implements PipeTransform {
    constructor(private framework: FrameworkService) { }
    transform(value: number): string {
        const MByte = 1024 * 1024;
        if (value === undefined || value === null) return '';
        if (value < 1024) return (value + 'b');
        if (value < MByte) return Math.round(value / 1024) + 'KB';
        return formatNumber(+(value / MByte), this.framework.CompanyInfo.NumberGeneral) + 'MB';
    }
}

@Pipe({ name: 'abs' })

export class AbsPipe implements PipeTransform {
    transform(value: number): number {
        return Math.abs(value);
    }
}

@Pipe({ name: 'truncate' })
export class TruncatePipe implements PipeTransform {
    constructor() {}
    transform(value: string): string {
        if (!value) return value;
        return truncate(value)
    }
}